import { IStyles, pxToRem, pxTovW, QuantityButtonV2 } from '@geneo2-web/shared-ui';
import { Box, Checkbox, FormControlLabel, Tooltip, Typography } from '@mui/material';
import { Question_QuestionEnum } from '@protos/content_management/content.db_pb';
import {
  AssessmentEnum,
  MarkingSchemeEnum,
  ModeOfConductionEnum,
} from '@protos/learning_management/lms.db_pb';
import { MarkingSchemeTooltip } from 'libs/shared-ui/src/components/composites/MarkingSchemeTooltip';
import { toStringQuestion } from '../../../../../utils/functions';
import { MAXIMUM_MARKS_LIMIT } from '../../../utils/assessment.utils';
import {
  getAllowedQuestionTypes,
  getMarkingSchemeOptionsForMode,
  getQuestionTypeTitleAndEnum,
  isMarkingSchemeAllowedForQuestionTypes,
  isMarkingSchemeChoiceEnabled,
  resolveSelectableMarkingScheme,
} from '../../ReviewAssessment/functions';

const questionTypeName = (type: Question_QuestionEnum) => {
  const title = getQuestionTypeTitleAndEnum(type)?.title;
  if (title) return title;
  const fallback = toStringQuestion(type);
  return fallback === 'QUESTION_TYPE_UNDEFINED' ? 'Unknown' : fallback;
};

const joinNames = (names: string[]) => {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

const getDisableReason = (props: {
  modeOfConduction?: ModeOfConductionEnum;
  markingScheme: MarkingSchemeEnum;
  questionTypes: Question_QuestionEnum[];
}) => {
  const allowedTypes = getAllowedQuestionTypes({
    modeOfConduction: props.modeOfConduction,
    markingScheme: props.markingScheme,
  });
  if (!allowedTypes) return undefined;

  const unsupportedNames = [
    ...new Set(
      props.questionTypes.filter((type) => !allowedTypes.includes(type))
    ),
  ].map(questionTypeName);
  if (unsupportedNames.length === 0) return undefined;

  const allowedNames = allowedTypes.map(questionTypeName);
  return `This marking scheme is disabled because this assessment includes ${joinNames(
    unsupportedNames
  )}. It supports only ${joinNames(allowedNames)}.`;
};

const styles: IStyles = {
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(10), md: pxTovW(16) },
  },
  options: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    flexWrap: 'wrap',
    gap: { xs: pxToRem(8), md: pxTovW(24) },
  },
  option: {
    margin: 0,
    gap: { xs: pxToRem(6), md: pxTovW(8) },
  },
};

export const MarkingSchemeChoice = (props: {
  modeOfConduction?: ModeOfConductionEnum;
  assessmentType?: AssessmentEnum;
  questionTypes: Question_QuestionEnum[];
  value?: MarkingSchemeEnum;
  onChange: (markingScheme: MarkingSchemeEnum) => void;
  marksPerQuestion: number;
  onMarksPerQuestionChange: (marks: number) => void;
}) => {
  const {
    modeOfConduction,
    assessmentType,
    questionTypes,
    value,
    onChange,
    marksPerQuestion,
    onMarksPerQuestionChange,
  } = props;

  if (
    !isMarkingSchemeChoiceEnabled({ modeOfConduction, assessmentType })
  )
    return null;

  const selectedScheme = resolveSelectableMarkingScheme({
    markingScheme: value,
    modeOfConduction,
    questionTypes,
  });

  return (
    <Box sx={styles.root}>
      <Typography variant="h3" fontWeight="bold">
        Marking Scheme
      </Typography>
      <Box sx={styles.options}>
        {getMarkingSchemeOptionsForMode(modeOfConduction).map((option) => {
          const enabled = isMarkingSchemeAllowedForQuestionTypes({
            modeOfConduction,
            markingScheme: option.type,
            questionTypes,
          });
          const disableReason = enabled
            ? undefined
            : getDisableReason({
                modeOfConduction,
                markingScheme: option.type,
                questionTypes,
              });
          const showInfoButton =
            option.type === MarkingSchemeEnum.MARKING_SCHEME_JEE;
          const choice = (
            <FormControlLabel
              sx={styles.option}
              disabled={!enabled}
              control={
                <Checkbox
                  checked={selectedScheme === option.type}
                  disabled={!enabled}
                  onChange={(event) => {
                    if (!event.target.checked || !enabled) return;
                    onChange(option.type);
                  }}
                  color="primary"
                />
              }
              label={
                <Typography
                  variant="h4"
                  sx={{ color: enabled ? 'inherit' : '#9E9E9E' }}
                >
                  {option.title}
                </Typography>
              }
            />
          );
          return (
            <Box
              key={option.type}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              {disableReason && !showInfoButton ? (
                <Tooltip
                  arrow
                  placement="top"
                  title={
                    <Typography variant="smallText" sx={{ color: '#FFFFFF' }}>
                      {disableReason}
                    </Typography>
                  }
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: '#000',
                        maxWidth: { xs: pxToRem(240), md: pxTovW(320) },
                        padding: { xs: pxToRem(8), md: pxTovW(10) },
                        borderRadius: pxToRem(8),
                      },
                    },
                    arrow: { sx: { color: '#000' } },
                  }}
                >
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    {choice}
                  </Box>
                </Tooltip>
              ) : (
                choice
              )}
              {showInfoButton && (
                <MarkingSchemeTooltip note={disableReason} />
              )}
            </Box>
          );
        })}
      </Box>
      {selectedScheme === MarkingSchemeEnum.MARKING_SCHEME_CUSTOM ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: pxTovW(12) }}>
          <Typography variant="h4">Marks per question</Typography>
          <QuantityButtonV2
            min={1}
            max={MAXIMUM_MARKS_LIMIT}
            value={marksPerQuestion}
            onChange={onMarksPerQuestionChange}
          />
        </Box>
      ) : null}
    </Box>
  );
};
